import { apiGet, apiPatch, apiPost } from './api-client.js';

/**
 * Typed wrappers over API_CONTRACT.md §13.10's `/platform/*` endpoints (I21) — `super_admin`
 * at `platform` scope only (`tenant.admin` / `audit.read_platform`). Shapes match
 * `api/src/modules/platform-admin/transport/platform-routes.ts`'s `tenantPayload` /
 * `platformAuditPayload` exactly.
 */

export const TENANT_STATUSES = ['active', 'suspended'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

export interface Tenant {
  id: string;
  codePrefix: string;
  nameNe: string;
  nameEn: string;
  status: string;
  defaultLocale: 'ne' | 'en';
  timezone: string;
  onboardedAt: string | null;
}

export function listTenants(signal?: AbortSignal): Promise<Tenant[]> {
  return apiGet<Tenant[]>('/platform/tenants', signal);
}

export interface OnboardTenantInput {
  codePrefix: string;
  nameNe: string;
  nameEn: string;
  defaultLocale?: 'ne' | 'en';
  rootOrgUnitCode: string;
  secretaryUsername: string;
  secretaryFullName: string;
  secretaryPassword: string;
  secretaryEmail?: string | null;
  allocationNotes?: string;
}

export interface OnboardTenantResult {
  tenant: Tenant;
  rootOrgUnitId: string;
  secretary: { id: string; username: string };
  mfaEnrolment: { token: string; expiresAt: string } | null;
}

export function onboardTenant(input: OnboardTenantInput, signal?: AbortSignal): Promise<OnboardTenantResult> {
  return apiPost<OnboardTenantResult>('/platform/tenants', input, signal);
}

export interface UpdateTenantInput {
  status?: TenantStatus;
  nameNe?: string;
  nameEn?: string;
}

export function updateTenant(tenantId: string, input: UpdateTenantInput, signal?: AbortSignal): Promise<Tenant> {
  return apiPatch<Tenant>(`/platform/tenants/${tenantId}`, input, signal);
}

export interface PlatformAuditEvent {
  id: string;
  occurredAt: string;
  chainSeq: string;
  affectedTenantId: string | null;
  crossTenant: boolean;
  category: string;
  action: string;
  outcome: string;
  actorUserId: string | null;
  actorKind: string;
  subjectType: string | null;
  subjectId: string | null;
  subjectLabel: string | null;
  correlationId: string | null;
  detail: Record<string, unknown> | null;
}

export interface PlatformAuditFilter {
  affectedTenantId?: string;
  actorUserId?: string;
  category?: string;
  action?: string;
  subjectType?: string;
  subjectId?: string;
  occurredFrom?: string;
  occurredTo?: string;
  limit?: number;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === '') continue;
    usp.set(key, String(value));
  }
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

export function listPlatformAuditEvents(
  filter: PlatformAuditFilter = {},
  signal?: AbortSignal,
): Promise<PlatformAuditEvent[]> {
  const query = buildQuery({ ...filter });
  return apiGet<PlatformAuditEvent[]>(`/platform/audit-events${query}`, signal);
}

export { ApiError, NetworkError } from './api-client.js';
